import { noop } from 'lodash';
import { useCallback } from 'react';

import {
   IconFillColor,
   IconWrapper,
   InteractiveTable,
   InteractiveTableCellProps,
   QuestionmarkCircleIcon,
   Tooltip,
   useInteractiveTableContext,
} from '@dreryk/react-components';

import { medicalServiceTranslationKeys } from '../../../../../translations';
import { MedicalServiceFormController } from '../../../form/medicalServiceForm.types';
import { createMedicalServiceSettlementTypeVisitor } from '../../../settlement/settlement.utils';
import {
   MedicalServicePractitionerBean,
   MedicalServicePractitionerUsingDefaultConfiguration,
} from '../../practitioner.types';

type MedicalServicePractitionerUsingDefaultConfigurationCellProps = InteractiveTableCellProps<MedicalServicePractitionerUsingDefaultConfiguration> &
   Pick<MedicalServiceFormController, 'control'>;

export const MedicalServicePractitionerUsingDefaultConfigurationCell = (
   props: MedicalServicePractitionerUsingDefaultConfigurationCellProps
): JSX.Element => {
   const { setCellValue, triggerCell } = useInteractiveTableContext<MedicalServicePractitionerBean>();
   const { defaultPrice, fixedValue, proportionalValue, taxRate, settlementType } = props.control.getValues();

   const synchronizeDefaultValues = useCallback(
      (synchronize: boolean) => {
         if (synchronize) {
            setCellValue('individualPrice', props.index, defaultPrice);
            triggerCell('individualPrice', props.index);

            setCellValue('settlementType', props.index, settlementType);
            triggerCell('settlementType', props.index);

            setCellValue('taxRate', props.index, taxRate);

            createMedicalServiceSettlementTypeVisitor(settlementType)({
               visitNone: noop,
               visitFixed: () => {
                  setCellValue('fixedValue', props.index, fixedValue);
                  triggerCell('fixedValue', props.index);
               },
               visitProportional: () => {
                  setCellValue('proportionalValue', props.index, proportionalValue);
                  triggerCell('proportionalValue', props.index);
               },
            });
         }
      },
      [props.index, setCellValue, triggerCell, defaultPrice, settlementType, taxRate, fixedValue, proportionalValue]
   );

   return (
      <InteractiveTable.CheckboxCell
         testId={props.testId}
         name={props.name}
         index={props.index}
         defaultValue={props.defaultValue}
         onSelectionChange={synchronizeDefaultValues}
      />
   );
};

export const MedicalServicePractitionerUsingDefaultConfigurationCellHeader = (): JSX.Element => {
   return (
      <Tooltip
         testId={'usingDefaultConfiguration'}
         content={medicalServiceTranslationKeys.shared.practitioner.table.headers.usingDefaultConfiguration.tooltip}
      >
         <IconWrapper testId={'usingDefaultConfiguration'} fillColor={IconFillColor.PRIMARY}>
            <QuestionmarkCircleIcon />
         </IconWrapper>
      </Tooltip>
   );
};
